import { isMetric } from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { useViewOverlayRef } from "@/hooks/use-view-overlay-ref";
import { PointCloudAnalysis } from "@/store/point-cloud-analysis-tool-slice";
import { getVolumeBasePlaneHeightReference } from "@/utils/colormap-analysis-utils";
import { grey } from "@faro-lotv/flat-ui";
import { VolumeMesh, VolumeMeshGeometry } from "@faro-lotv/lotv";
import { useEffect, useMemo, useState, type JSX } from "react";
import { Color, Group, Plane, Vector3 } from "three";
import {
  LineMaterial,
  LineSegments2,
  LineSegmentsGeometry,
} from "three-stdlib";
import { TextLabel } from "../text-label";
import {
  computeMeshOuterBoundaryLoops,
  loopsToSegments,
} from "./volume-outline-geometry";

type VolumeAnalysisRendererProps = {
  analysis: PointCloudAnalysis;
  volumeGeometry: VolumeMeshGeometry;
};

/** @returns The renderer of volume analysis on point cloud */
export function VolumeAnalysisRenderer({
  analysis,
  volumeGeometry,
}: VolumeAnalysisRendererProps): JSX.Element | null {
  const [volumeMesh] = useState(() => {
    const colormap = analysis.colormap.map(({ value, color }) => ({
      value,
      color: new Color(color),
    }));
    return new VolumeMesh(
      volumeGeometry,
      colormap,
      analysis.volumeDisplayNbIsoLines,
    );
  });

  useEffect(
    () => () => {
      volumeMesh.geometry.dispose();
      volumeMesh.material.dispose();
    },
    [volumeMesh],
  );

  const [boundaries, setBoundaries] = useState<Group>();
  useEffect(() => {
    if (!analysis.volumeAnalysisResult) {
      setBoundaries(undefined);
      return;
    }

    const polygonSelectionLocal = analysis.polygonSelection.map((p) =>
      new Vector3().fromArray(p).sub(volumeMesh.position),
    );

    const lineSegmentsGeometry = new LineSegmentsGeometry();
    lineSegmentsGeometry.setPositions(loopsToSegments([polygonSelectionLocal]));
    const meshBoundaries = new LineSegments2(
      lineSegmentsGeometry,
      new LineMaterial({
        color: 0xffffff,
        linewidth: 2,
      }),
    );

    const basePlaneOrigin = new Vector3().fromArray(
      analysis.volumeAnalysisResult.basePlaneOrigin,
    );
    basePlaneOrigin.sub(volumeMesh.position);
    const basePlaneNormal = new Vector3().fromArray(
      analysis.volumeAnalysisResult.basePlaneNormal,
    );
    basePlaneOrigin.addScaledVector(
      basePlaneNormal,
      getVolumeBasePlaneHeightReference(analysis),
    );

    const basePlane = new Plane().setFromNormalAndCoplanarPoint(
      basePlaneNormal,
      basePlaneOrigin,
    );

    const polygonSelectionProjected = polygonSelectionLocal.map((p) =>
      basePlane.projectPoint(p, new Vector3()),
    );

    const lineSegmentsGeometry2 = new LineSegmentsGeometry();
    lineSegmentsGeometry2.setPositions(
      loopsToSegments([polygonSelectionProjected]),
    );
    const meshBoundaries2 = new LineSegments2(
      lineSegmentsGeometry2,
      new LineMaterial({
        color: 0xffffff,
        linewidth: 2,
      }),
    );

    const group = new Group();
    group.add(meshBoundaries);
    group.add(meshBoundaries2);
    group.position.copy(volumeMesh.position);

    const analysisShapeOutlines = computeMeshOuterBoundaryLoops(
      volumeMesh.geometry,
      basePlaneNormal,
    );
    let shapeBoundaries: LineSegments2 | undefined;
    if (analysisShapeOutlines.length > 0) {
      const shapeGeometry = new LineSegmentsGeometry();
      shapeGeometry.setPositions(loopsToSegments(analysisShapeOutlines));
      shapeBoundaries = new LineSegments2(
        shapeGeometry,
        new LineMaterial({
          color: 0xffffff,
          linewidth: 2,
        }),
      );
      group.add(shapeBoundaries);
    }

    setBoundaries(group);
    return () => {
      meshBoundaries.geometry.dispose();
      meshBoundaries.material.dispose();
      meshBoundaries2.geometry.dispose();
      meshBoundaries2.material.dispose();
      shapeBoundaries?.geometry.dispose();
      shapeBoundaries?.material.dispose();
    };
  }, [
    analysis,
    analysis.volumeAnalysisResult,
    analysis.volumeBasePlaneType,
    analysis.polygonSelection,
    volumeMesh,
  ]);

  const colormap = useMemo(
    () =>
      analysis.colormap.map(({ value, color }) => ({
        value,
        color: new Color(color),
      })),
    [analysis.colormap],
  );

  useEffect(() => {
    volumeMesh.colormap = colormap;
  }, [colormap, volumeMesh]);

  useEffect(() => {
    volumeMesh.numberOfIsolines = analysis.volumeDisplayNbIsoLines ?? 0;
  }, [analysis.volumeDisplayNbIsoLines, volumeMesh]);

  const [meshPosition, setMeshPosition] = useState<Vector3>();
  const [labelPosition, setLabelPosition] = useState<Vector3>();

  useEffect(() => {
    if (!analysis.volumeAnalysisResult) return;

    const basePlaneNormal = new Vector3().fromArray(
      analysis.volumeAnalysisResult.basePlaneNormal,
    );
    const position = new Vector3(0, 0, 0);
    // Default to a small lift-up to avoid z-fighting with points
    const smallOffset = 0.01;
    position.addScaledVector(
      basePlaneNormal,
      analysis.volumeDisplayOffset ?? smallOffset,
    );
    setMeshPosition(position);

    const labelPosition = new Vector3().fromArray(
      analysis.volumeAnalysisResult.basePlaneOrigin,
    );
    labelPosition.addScaledVector(
      basePlaneNormal,
      (analysis.volumeAnalysisResult.heightMax +
        analysis.volumeAnalysisResult.heightMin) /
        2 +
        (analysis.volumeDisplayOffset ?? 0.0),
    );

    setLabelPosition(labelPosition);
  }, [analysis.volumeAnalysisResult, analysis.volumeDisplayOffset]);

  const [volumeText, setVolumeText] = useState<string>();
  const distanceUnit = useDistanceUnit();
  useEffect(() => {
    if (!analysis.volumeAnalysisResult) {
      setVolumeText(undefined);
      return;
    }
    const heightReference = getVolumeBasePlaneHeightReference(analysis);
    const volume =
      analysis.volumeAnalysisResult.volume -
      heightReference * analysis.volumeAnalysisResult.baseArea;
    if (isMetric(distanceUnit)) {
      setVolumeText(`~${volume.toFixed(1)} m³`);
    } else {
      const cubicMeterToCubicFeet = 35.3147;
      const volumeInCubicFeet = volume * cubicMeterToCubicFeet;
      setVolumeText(`~${volumeInCubicFeet.toFixed(1)} ft³`);
    }
  }, [analysis, analysis.volumeAnalysisResult, distanceUnit]);

  const labelContainer = useViewOverlayRef();
  return (
    <>
      <group position={meshPosition}>
        <primitive object={volumeMesh} />
        {boundaries && <primitive object={boundaries} />}
      </group>
      {labelPosition && (
        <TextLabel
          index={0}
          position={labelPosition}
          parentRef={labelContainer}
          visible
          active
          transparent
          pointerEvents="none"
          backgroundColor={grey[1000]}
        >
          {volumeText}
        </TextLabel>
      )}
    </>
  );
}
